import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import {
  clientContactsApi,
  type CreateClientContactRequest,
} from "@/lib/client-contacts";

export interface NewClientContactDialogProps {
  clientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new contact id on successful POST. */
  onCreated: (newContactId: number) => void;
}

export function NewClientContactDialog({
  clientId,
  open,
  onOpenChange,
  onCreated,
}: NewClientContactDialogProps) {
  const qc = useQueryClient();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");

  const createMut = useMutation({
    mutationFn: (body: CreateClientContactRequest) =>
      clientContactsApi.create(clientId, body),
    onSuccess: (created) => {
      toast.success("Contact added");
      void qc.invalidateQueries({ queryKey: ["client-contacts", clientId] });
      onCreated(created.id);
      // Reset fields & close
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setMobile("");
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(`Add failed: ${err.message}`),
  });

  const handleSave = () => {
    if (!firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    createMut.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      mobile: mobile.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add contact</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <DialogField
            id="nc-fn"
            label="First name"
            value={firstName}
            onChange={setFirstName}
          />
          <DialogField
            id="nc-ln"
            label="Last name"
            value={lastName}
            onChange={setLastName}
          />
          <DialogField
            id="nc-em"
            label="Email"
            value={email}
            onChange={setEmail}
          />
          <DialogField
            id="nc-ph"
            label="Phone"
            value={phone}
            onChange={setPhone}
          />
          <DialogField
            id="nc-mb"
            label="Mobile"
            value={mobile}
            onChange={setMobile}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setFirstName("");
              setLastName("");
              setEmail("");
              setPhone("");
              setMobile("");
              onOpenChange(false);
            }}
            disabled={createMut.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Save Contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
